import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarPlus, Loader2 } from 'lucide-react';
import { createAgenda } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import MarcaDeLaAgenda from './MarcaDeLaAgenda';
import ProfesionalAgenda from './ProfesionalAgenda';
import AdmiteVisitanteCasilla from './AdmiteVisitanteCasilla';

/**
 * NUEVA AGENDA (Gestión de agendas, solo admin, 30/09/2026).
 *
 * Hasta hoy una agenda solo nacía por semilla. Aquí se da de alta con su
 * nombre, su profesional (un usuario del equipo o, si aún no tiene, su nombre
 * escrito), su tipo (consulta o quirófano) y su color, y se puede «Copiar la
 * configuración de…» otra agenda: hueco, horario semanal, duraciones por tipo
 * de cita, límites de quirófano y descripción (y la consulta conjunta con
 * enfermería, que se enseña para decidirla aquí). Los tipos de cita son los
 * del catálogo, los mismos en todas las agendas: lo propio de cada una son sus
 * duraciones, y eso es lo que se copia.
 *
 * La marca de los mensajes al paciente y su candado se eligen aquí, como en
 * Configurar agenda; no se copian. Tampoco el profesional, el color ni el
 * alquiler de módulo. El `code` lo pone el servidor, único, a partir del
 * nombre. La agenda nace activa y al final del orden.
 *
 * «Admite médico visitante» (07/10/2026) se elige aquí también, sin marcar
 * por defecto; no se copia de la otra agenda.
 *
 * Props:
 *  - open / onOpenChange
 *  - agendas:   las agendas que ya hay (para «Copiar la configuración de…»)
 *  - onCreated: callback con la agenda creada
 */

const COLOR_POR_DEFECTO = '#2E6E5E';

function resumenCopia(origen, tipo) {
  if (!origen) {
    return 'Sin copiar, nace cerrada toda la semana: ponle después el horario con «Configurar».';
  }
  const duraciones = Object.keys(origen.concept_overrides || {}).length;
  const partes = [
    `hueco de ${origen.slot_minutes || 15} min`,
    'horario semanal',
    duraciones === 1 ? '1 duración por tipo de cita' : `${duraciones} duraciones por tipo de cita`,
  ];
  if (tipo === 'quirofano' && (origen.max_qx_manana || origen.max_qx_tarde)) {
    partes.push(`límites de quirófano (mañana ${origen.max_qx_manana || 'sin límite'}, tarde ${origen.max_qx_tarde || 'sin límite'})`);
  }
  if ((origen.descripcion || '').trim()) partes.push('lo que se hace en ella');
  return `Se copian de «${origen.name}»: ${partes.join(', ')}. No se copian el nombre, el profesional, `
    + 'el color, la marca ni el alquiler de módulo.';
}

export default function AgendaNuevaDialog({ open, onOpenChange, agendas = [], onCreated }) {
  const [name, setName] = useState('');
  const [tipo, setTipo] = useState('consulta');
  const [color, setColor] = useState(COLOR_POR_DEFECTO);
  const [profesional, setProfesional] = useState({ userId: '', nombre: '' });
  const [copiarDe, setCopiarDe] = useState(''); // '' = no copiar
  const [conjunta, setConjunta] = useState(false);
  const [marcaClave, setMarcaClave] = useState('');
  const [marcaBloqueada, setMarcaBloqueada] = useState(false);
  const [admiteVisitante, setAdmiteVisitante] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setTipo('consulta');
    setColor(COLOR_POR_DEFECTO);
    setProfesional({ userId: '', nombre: '' });
    setCopiarDe('');
    setConjunta(false);
    setMarcaClave('');
    setMarcaBloqueada(false);
    setAdmiteVisitante(false);
    setSaving(false);
  }, [open]);

  const origen = agendas.find((a) => a.id === copiarDe) || null;

  // Elegir de dónde copiar propone su mismo tipo y su consulta conjunta (se
  // pueden cambiar después, aquí mismo).
  const elegirOrigen = (id) => {
    setCopiarDe(id);
    const a = agendas.find((x) => x.id === id);
    if (!a) return;
    const tipoOrigen = a.tipo === 'quirofano' ? 'quirofano' : 'consulta';
    setTipo(tipoOrigen);
    setConjunta(tipoOrigen === 'consulta' && Boolean(a.consulta_conjunta));
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error('El nombre de la agenda es obligatorio');
      return;
    }
    if (!profesional.userId && !profesional.nombre.trim()) {
      toast.error('Falta el profesional: elige su usuario o, si aún no tiene, escribe su nombre');
      return;
    }
    setSaving(true);
    try {
      const nueva = await createAgenda({
        name: name.trim(),
        tipo,
        color,
        doctor_user_id: profesional.userId || null,
        doctor_name: profesional.nombre.trim(),
        copiar_de: copiarDe || null,
        consulta_conjunta: tipo === 'consulta' ? conjunta : false,
        marca_clave: marcaClave,
        marca_bloqueada: marcaBloqueada,
        admite_visitante: admiteVisitante,
      });
      toast.success(`Agenda «${nueva?.name || name.trim()}» creada`);
      onCreated?.(nueva);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la agenda');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="agenda-nueva-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 text-[#13231E]" />
            Nueva agenda
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Nombre + color */}
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="agenda-nueva-name">Nombre</Label>
              <Input
                id="agenda-nueva-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                placeholder="Ej.: Consulta — Dra. Nombre Apellido"
                className="mt-1.5"
                data-testid="agenda-nueva-name"
              />
            </div>
            <div>
              <Label>Color</Label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1.5 block h-10 w-14 rounded-md border border-input bg-white p-1 cursor-pointer"
                aria-label="Color de la agenda"
                data-testid="agenda-nueva-color"
              />
            </div>
          </div>

          <ProfesionalAgenda
            prefijo="agenda-nueva"
            userId={profesional.userId}
            nombre={profesional.nombre}
            onChange={setProfesional}
            ayuda="¿Aún no tiene usuario? Escribe su nombre: la agenda funciona igual (la ven y citan en ella administración, recepción y enfermería). Cuando lo tenga, elígelo en «Configurar»."
          />

          <Separator />

          {/* Copiar la configuración de otra agenda */}
          <div>
            <Label htmlFor="agenda-nueva-copiar">Copiar la configuración de…</Label>
            <select
              id="agenda-nueva-copiar"
              value={copiarDe}
              onChange={(e) => elegirOrigen(e.target.value)}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
              data-testid="agenda-nueva-copiar"
            >
              <option value="">No copiar: empezar vacía</option>
              {agendas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.tipo === 'quirofano' ? ' (quirófano)' : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1" data-testid="agenda-nueva-resumen-copia">
              {resumenCopia(origen, tipo)} Los tipos de cita son los del catálogo, los mismos en todas
              las agendas: lo que cambia de una a otra es su duración.
            </p>
          </div>

          {/* Tipo */}
          <div>
            <Label htmlFor="agenda-nueva-tipo">Tipo</Label>
            <select
              id="agenda-nueva-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
              data-testid="agenda-nueva-tipo"
            >
              <option value="consulta">Consulta</option>
              <option value="quirofano">Quirófano</option>
            </select>
            {origen && (origen.tipo === 'quirofano' ? 'quirofano' : 'consulta') !== tipo && (
              <p className="text-xs text-amber-700 mt-1" data-testid="agenda-nueva-aviso-tipo">
                «{origen.name}» es {origen.tipo === 'quirofano' ? 'de quirófano' : 'de consulta'}: se copian
                igual su horario y sus duraciones{tipo === 'consulta' ? ', pero no sus límites de quirófano' : ''}.
              </p>
            )}
            {tipo === 'quirofano' && (
              <p className="text-xs text-muted-foreground mt-1">
                En quirófano cada día se fija su lugar, y las citas lo heredan.
              </p>
            )}
          </div>

          {tipo === 'consulta' && (
            <label className="flex items-start gap-2 text-sm" htmlFor="agenda-nueva-conjunta">
              <input
                id="agenda-nueva-conjunta"
                type="checkbox"
                className="mt-0.5"
                checked={conjunta}
                onChange={(e) => setConjunta(e.target.checked)}
                data-testid="agenda-nueva-conjunta"
              />
              <span>
                <span className="font-medium text-[#13231E]">Consulta conjunta con enfermería</span>
                <span className="block text-xs text-muted-foreground">
                  Enfermería ve primero a sus pacientes: le salen en su agenda y los marca como vistos.
                </span>
              </span>
            </label>
          )}

          <AdmiteVisitanteCasilla
            prefijo="agenda-nueva"
            checked={admiteVisitante}
            onChange={setAdmiteVisitante}
          />

          <Separator />

          <MarcaDeLaAgenda
            prefijo="agenda-nueva"
            marcaClave={marcaClave}
            onMarcaClave={setMarcaClave}
            marcaBloqueada={marcaBloqueada}
            onMarcaBloqueada={setMarcaBloqueada}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="agenda-nueva-cancel">
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="agenda-nueva-save"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Crear agenda
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
